import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatTRY } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Wrench,
  ShieldCheck,
  Wallet,
  Scale,
  CheckCircle2,
  Clock,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  FileDown,
  TrendingUp,
  TrendingDown,
  Package,
  Store,
} from "lucide-react";

const MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

const StatCard = ({ testId, title, subtitle, icon: Icon, iconBg = "bg-slate-900", children }) => (
  <section data-testid={testId} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 p-5 border-b border-slate-100 bg-slate-50">
      <div className={`w-10 h-10 rounded-lg text-white flex items-center justify-center ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h2 className="font-display text-base font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const TwoColSplit = ({ left, right }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
    {left}
    {right}
  </div>
);

const Column = ({ icon: Icon, iconColor = "text-slate-600", tint = "", label, amount, count, countLabel, testIdAmount, testIdCount, carried, testIdCarried }) => (
  <div className={`p-6 ${tint}`}>
    <div className="flex items-center gap-1.5">
      <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">{label}</p>
    </div>
    <p data-testid={testIdAmount} className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-slate-900">
      {formatTRY(amount)}
    </p>
    {count !== undefined && (
      <p className="text-xs text-slate-500 mt-1">
        <span data-testid={testIdCount} className="font-semibold font-mono-num">{count}</span> {countLabel}
      </p>
    )}
    {carried && carried.count > 0 && (
      <div
        data-testid={testIdCarried}
        className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-[11px]"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
        <span className="text-rose-700 font-semibold">
          {carried.count} adet geçmişten devir · <span className="font-mono-num">{formatTRY(carried.total)}</span>
        </span>
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/summary");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Pencere veya sekme geri aktif olduğunda özet verisini yeniden yükle
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") refresh();
    });
    return () => {
      window.removeEventListener("focus", refresh);
    };
  }, []);

  if (loading || !data) {
    return <div data-testid="dashboard-loading" className="py-12 text-center text-slate-500">Yükleniyor…</div>;
  }

  const { balance, insurance, salary_status: sal, totals, jobs_summary: jobs } = data;

  const scopeLabel = "Tüm Zamanlar";
  const isMonth = false; // Dashboard sadece Tüm Zamanlar — aylık için /periods
  const jobsCarried = { count: 0, total: 0 };
  const insCarried = { count: 0, total: 0 };
  const cm = {}; // Aylık veri Dashboard'da artık kullanılmıyor

  // İş kayıtları — Tüm Zamanlar
  const jobsBucket = jobs.by_status_all;
  const jobsPaid = jobsBucket.tahsil_edildi || { count: 0, total: 0 };
  const jobsPartial = jobsBucket.kismi_odeme || { count: 0, total: 0 };
  const jobsDelivered = jobsBucket.teslim_edildi || { count: 0, total: 0 };
  const jobsPending = {
    count: jobsBucket.beklemede.count + jobsDelivered.count + jobsPartial.count,
    total: jobsBucket.beklemede.total + jobsDelivered.total + jobsPartial.total,
  };
  const jobsTotalCount = jobsPaid.count + jobsPending.count;

  // Sigorta — Tüm Zamanlar
  const insCollected = insurance.alltime_collected;
  const insPending = insurance.alltime_pending;
  const insCaseCount = insurance.alltime_case_count;
  const insCollectedCount = insurance.collected_count;
  const insPendingCount = Math.max(insurance.alltime_case_count - insurance.collected_count, 0);

  // Hero — Tüm Zamanlar Net Bakiye (sadece gerçekleşen cash hareketleri)
  const heroBalance = balance.net_bakiye || 0;
  const heroAlacak = balance.total_alacak || 0;

  // Grand total — Tüm Zamanlar
  const incomeJobs = totals.jobs_income || 0;
  const incomeIns = insurance.alltime_collected || 0;
  const incomeLoan = totals.loan_collected || 0;
  const expenseTotal = totals.expense;
  const expenseSalary = totals.salary_expense;
  const expenseSupply = totals.supply_expense;
  const expenseShop = totals.shop_expense || 0;
  const expenseLoan = totals.loan_paid || 0;
  const netProfit = (incomeJobs + incomeIns + incomeLoan) - expenseTotal;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* HEADER — Tüm Zamanlar genel özet */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Genel Özet</p>
          <p className="text-sm text-slate-700">
            Görünüm: <span data-testid="active-scope-label" className="font-semibold text-slate-900">Tüm Zamanlar</span> ·
            {" "}
            <Link to="/periods" className="text-blue-600 hover:underline" data-testid="link-periods">
              Ay ay rapor için → Geçmiş Dönemler
            </Link>
          </p>
        </div>
      </div>

      {/* HERO — GENEL BAKİYE */}
      <section
        data-testid="genel-bakiye-hero"
        className="rounded-xl p-6 sm:p-8 border bg-gradient-to-br from-slate-900 to-slate-800 border-slate-800 text-white"
      >
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-slate-300" />
          <p className="text-[11px] uppercase tracking-widest text-slate-300 font-semibold">
            Net Bakiye · Tahsil Edilen
          </p>
        </div>
        <p
          data-testid="genel-bakiye-value"
          className="font-display text-3xl sm:text-5xl font-bold mt-2 font-mono-num text-white"
        >
          {formatTRY(heroBalance)}
        </p>
        <p className="text-xs text-slate-300 mt-2">
          Gelir {formatTRY(totals.income)}
          {" − "}Gider {formatTRY(totals.expense)}
          <span className="ml-2 text-slate-400">· {scopeLabel}</span>
        </p>
        {heroAlacak > 0 && (
          <div className="mt-3 border-t border-slate-700 pt-3">
            <p
              data-testid="hero-pending-total"
              className="font-display text-xl sm:text-2xl font-bold font-mono-num text-amber-300"
            >
              {formatTRY(heroAlacak)}
            </p>
            <p className="text-xs text-amber-200/80 mt-1">
              Ek olarak bekleyen alacak (
              <span className="font-mono-num">{formatTRY(balance.customer_alacak || 0)}</span> iş kaydı{" "}
              +{" "}
              <span className="font-mono-num">{formatTRY(insurance.total_pending || 0)}</span> sigorta{" "}
              +{" "}
              <span className="font-mono-num">{formatTRY(balance.lent_alacak || 0)}</span> finans) — henüz tahsil edilmediği için Net Kasa'ya dahil değildir.
            </p>
          </div>
        )}
        {((balance.supplier_debt || 0) + (balance.shop_debt || 0) + (balance.borrowed_debt || 0)) > 0 && (
          <div className="mt-3 border-t border-slate-700 pt-3">
            <p
              data-testid="hero-pending-payment"
              className="font-display text-xl sm:text-2xl font-bold font-mono-num text-rose-300"
            >
              − {formatTRY((balance.supplier_debt || 0) + (balance.shop_debt || 0) + (balance.borrowed_debt || 0))}
            </p>
            <p className="text-xs text-rose-200/80 mt-1">
              Henüz ödenmemiş borçlarınız (
              <span className="font-mono-num">{formatTRY(balance.supplier_debt || 0)}</span> malzeme{" "}
              +{" "}
              <span className="font-mono-num">{formatTRY(balance.shop_debt || 0)}</span> dükkan{" "}
              +{" "}
              <span className="font-mono-num">{formatTRY(balance.borrowed_debt || 0)}</span> alınan borç) — bilgi amaçlı, Net Bakiye'ye dahil değildir.
            </p>
          </div>
        )}
      </section>

      {/* İŞ KAYITLARI · Ödenen / Bekleyen */}
      <StatCard
        testId="jobs-summary-card"
        title="İş Kayıtları"
        subtitle={`${scopeLabel} · ${jobsTotalCount} kayıt${isMonth && jobsCarried.count > 0 ? " · Geçmiş aylardan bekleyen alacaklar bu aya devredildi" : ""}`}
        icon={Wrench}
        iconBg="bg-emerald-600"
      >
        <TwoColSplit
          left={
            <Column
              tint="bg-emerald-50/40"
              icon={CheckCircle2}
              iconColor="text-emerald-600"
              label="Tahsil Edilen"
              amount={jobsPaid.total}
              count={jobsPaid.count}
              countLabel="iş tahsil edildi"
              testIdAmount="jobs-paid-amount"
              testIdCount="jobs-paid-count"
            />
          }
          right={
            <Column
              tint="bg-amber-50/40"
              icon={Clock}
              iconColor="text-amber-600"
              label="Bekleyen"
              amount={jobsPending.total}
              count={jobsPending.count}
              countLabel="iş henüz teslim edilmedi"
              testIdAmount="jobs-pending-amount"
              testIdCount="jobs-pending-count"
              carried={jobsCarried}
              testIdCarried="jobs-carried-over"
            />
          }
        />
      </StatCard>

      {/* SİGORTA ALACAKLARI · Ödenen / Bekleyen */}
      <StatCard
        testId="insurance-summary-card"
        title="Sigorta Alacakları"
        subtitle={`${scopeLabel} · ${insCaseCount} dosya${isMonth && insCarried.count > 0 ? " · Geçmiş aylardan bekleyen alacaklar bu aya devredildi" : ""}`}
        icon={ShieldCheck}
        iconBg="bg-blue-600"
      >
        <TwoColSplit
          left={
            <Column
              tint="bg-emerald-50/40"
              icon={CheckCircle2}
              iconColor="text-emerald-600"
              label="Ödenen (Tahsil Edildi)"
              amount={insCollected}
              count={insCollectedCount}
              countLabel="dosya tahsil edildi"
              testIdAmount="ins-paid-amount"
              testIdCount="ins-paid-count"
            />
          }
          right={
            <Column
              tint="bg-amber-50/40"
              icon={Clock}
              iconColor="text-amber-600"
              label="Bekleyen Alacak"
              amount={insPending}
              count={insPendingCount}
              countLabel="dosya bekliyor"
              testIdAmount="ins-pending-amount"
              testIdCount="ins-pending-count"
              carried={insCarried}
              testIdCarried="ins-carried-over"
            />
          }
        />
      </StatCard>

      {/* MALZEME GİDERLERİ · Parça / Boyahane / Kaporta */}
      <StatCard
        testId="supplies-summary-card"
        title="Malzeme Giderleri"
        subtitle={`${scopeLabel} · Parça, Boyahane ve Kaporta`}
        icon={Package}
        iconBg="bg-amber-600"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <Column
            tint="bg-emerald-50/40"
            icon={Package}
            iconColor="text-emerald-700"
            label="Parça"
            amount={isMonth ? cm.supply_parca : totals.supply_parca}
            testIdAmount="supply-parca-amount"
          />
          <Column
            tint="bg-amber-50/40"
            icon={Package}
            iconColor="text-amber-700"
            label="Boyahane Malzeme"
            amount={isMonth ? cm.supply_boyahane : totals.supply_boyahane}
            testIdAmount="supply-boyahane-amount"
          />
          <Column
            tint="bg-blue-50/40"
            icon={Package}
            iconColor="text-blue-700"
            label="Kaporta Malzeme"
            amount={isMonth ? cm.supply_kaporta : totals.supply_kaporta}
            testIdAmount="supply-kaporta-amount"
          />
        </div>
        {isMonth && (balance.supplier_debt_carried || 0) > 0 && (
          <Link
            to="/supplies"
            data-testid="supply-devir-note"
            className="mx-6 mb-4 flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-[11px] hover:bg-rose-100 transition-colors cursor-pointer"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-rose-700 font-semibold">
              {balance.supplier_debt_carried_count} adet geçmişten devreden ödenmemiş borç bu ayın giderine dahil edildi
              {" · "}
              <span className="font-mono-num">{formatTRY(balance.supplier_debt_carried)}</span>
              {" → "}listeyi görmek için tıkla
            </span>
          </Link>
        )}
        {!isMonth && (balance.supplier_debt || 0) > 0 && (
          <div className="px-6 py-3 bg-rose-50 border-t border-rose-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              <p className="text-[11px] uppercase tracking-widest font-semibold text-rose-700">
                Ödenmemiş Malzeme Borçu
              </p>
            </div>
            <span data-testid="supply-debt-total" className="font-display text-base font-bold font-mono-num text-rose-700">
              {formatTRY(balance.supplier_debt)}
            </span>
          </div>
        )}
      </StatCard>

      {/* DÜKKAN GİDERLERİ */}
      <StatCard
        testId="shop-expenses-summary-card"
        title="Dükkan Giderleri"
        subtitle={`${scopeLabel} · Kira, fatura, işletme`}
        icon={Store}
        iconBg="bg-rose-600"
      >
        <div className="p-6">
          <div className="flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5 text-rose-600" />
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">Toplam Dükkan Gideri</p>
          </div>
          <p data-testid="shop-expense-amount" className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-slate-900">
            {formatTRY(expenseShop)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {isMonth
              ? `Bu ay ödenen: ${formatTRY((cm.shop || 0) - (balance.shop_debt_carried || 0))}${(balance.shop_debt_carried || 0) > 0 ? ` · Devir borç: ${formatTRY(balance.shop_debt_carried)}` : ""}`
              : "Tüm zaman toplamı"}
          </p>
          {isMonth && (balance.shop_debt_carried || 0) > 0 && (
            <Link
              to="/shop-expenses"
              data-testid="shop-devir-note"
              className="mt-3 flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-[11px] hover:bg-rose-100 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-rose-700 font-semibold">
                {balance.shop_debt_carried_count} adet geçmişten devreden ödenmemiş borç bu ayın giderine dahil edildi
                {" → "}listeyi görmek için tıkla
              </span>
            </Link>
          )}
        </div>
        {!isMonth && (balance.shop_debt || 0) > 0 && (
          <div className="px-6 py-3 bg-rose-50 border-t border-rose-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              <p className="text-[11px] uppercase tracking-widest font-semibold text-rose-700">
                Ödenmemiş Dükkan Borçu
              </p>
            </div>
            <span data-testid="shop-debt-total" className="font-display text-base font-bold font-mono-num text-rose-700">
              {formatTRY(balance.shop_debt)}
            </span>
          </div>
        )}
      </StatCard>

      {/* FİNANS · Borç / Alacak */}
      <StatCard
        testId="finans-summary-card"
        title="Finans · Borç / Alacak"
        subtitle={`${scopeLabel} · Alınan-verilen borç hareketleri`}
        icon={Wallet}
        iconBg="bg-indigo-600"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="p-6">
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-rose-700">Alınan Borç · Gider Kısmı</p>
            </div>
            <p data-testid="finans-loan-paid" className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-rose-700">
              {formatTRY(expenseLoan)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {isMonth
                ? `Bu ay ödenen: ${formatTRY((cm.loan_paid || 0) - (balance.borrowed_debt_in_expense || 0))}${(balance.borrowed_debt_in_expense || 0) > 0 ? ` · Devir borç: ${formatTRY(balance.borrowed_debt_in_expense)}` : ""} · Kalan: ${formatTRY(balance.borrowed_debt || 0)}`
                : <>Ödenen kısım gidere yansır · Kalan borç: <span className="font-mono-num font-semibold text-rose-700">{formatTRY(balance.borrowed_debt || 0)}</span></>}
            </p>
            {isMonth && (balance.borrowed_debt_carried || 0) > 0 && (
              <Link
                to="/finans"
                data-testid="loan-debt-carried"
                className="mt-3 flex items-center gap-1.5 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-[11px] hover:bg-rose-100 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-rose-700 font-semibold">
                  {balance.borrowed_debt_carried_count} adet geçmişten devreden ödenmemiş borç bu ayın giderine dahil edildi
                  {" · "}
                  <span className="font-mono-num">{formatTRY(balance.borrowed_debt_carried)}</span>
                  {" → "}listeyi görmek için tıkla
                </span>
              </Link>
            )}
          </div>
          <div className="p-6">
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">Verilen Borç · Gelir Kısmı</p>
            </div>
            <p data-testid="finans-loan-collected" className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-emerald-700">
              {formatTRY(incomeLoan)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Geri alınan kısım gelire yansır · Kalan alacak: <span className="font-mono-num font-semibold text-emerald-700">{formatTRY(balance.lent_alacak || 0)}</span>
            </p>
          </div>
        </div>
      </StatCard>

      {/* MAAŞ GİDERLERİ · Haftalık (Ödenen) */}
      <StatCard
        testId="salary-summary-card"
        title="Maaş Giderleri · Haftalık"
        subtitle={`${sal.active_employees} aktif çalışan · Ödendikçe gider olarak işlenir`}
        icon={Wallet}
        iconBg="bg-slate-900"
      >        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="p-6">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">Haftalık Toplam</p>
            </div>
            <p data-testid="salary-weekly-total" className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-slate-900">
              {formatTRY(sal.weekly_total)}
            </p>
            <p className="text-xs text-slate-500 mt-1">Her hafta ödenmesi gereken toplam</p>
          </div>
          <div className="p-6 bg-emerald-50/40">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">
                {isMonth ? "Bu Ay Ödenen" : "Tüm Zaman Ödenen"}
              </p>
            </div>
            <p data-testid="salary-paid-month" className="font-display text-2xl sm:text-3xl font-bold mt-3 font-mono-num text-emerald-700">
              {formatTRY(isMonth ? sal.paid_month : totals.salary_expense)}
            </p>
            <p className="text-xs text-emerald-800/70 mt-1">Gidere eklendi</p>
          </div>
        </div>
      </StatCard>

      {/* EN SONDA · TOPLAM GELİR & GİDER */}
      <section
        data-testid="grand-total-card"
        className="rounded-xl border border-slate-200 shadow-sm bg-white overflow-hidden"
      >
        <div className="p-5 border-b border-slate-100 bg-slate-50">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Genel Toplam · {scopeLabel}
          </p>
          <h2 className="font-display text-base font-bold text-slate-900">Gelir & Gider Özeti</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-emerald-700">
              <ArrowUpRight className="w-4 h-4" />
              <p className="text-[11px] uppercase tracking-widest font-semibold">Toplam Gelir</p>
            </div>
            <p data-testid="grand-income" className="font-display text-3xl sm:text-4xl font-bold text-emerald-700 mt-3 font-mono-num">
              {formatTRY(incomeJobs + incomeIns + incomeLoan)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              İş gelirleri {formatTRY(incomeJobs)} · Sigorta {formatTRY(incomeIns)} · Finans {formatTRY(incomeLoan)}
            </p>
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-slate-700">
              <ArrowDownRight className="w-4 h-4" />
              <p className="text-[11px] uppercase tracking-widest font-semibold">Toplam Gider</p>
            </div>
            <p data-testid="grand-expense" className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3 font-mono-num">
              {formatTRY(expenseTotal)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Maaş {formatTRY(expenseSalary)} · Malzeme {formatTRY(expenseSupply)} · Dükkan {formatTRY(expenseShop)} · Finans {formatTRY(expenseLoan)}
            </p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            {netProfit >= 0
              ? <TrendingUp className="w-4 h-4 text-emerald-400" />
              : <TrendingDown className="w-4 h-4 text-rose-400" />}
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-300">
              Net Kâr ({scopeLabel})
            </span>
          </div>
          <span
            data-testid="grand-net"
            className="font-display text-xl sm:text-2xl font-bold font-mono-num text-white"
          >
            {formatTRY(netProfit)}
          </span>
        </div>
      </section>
    </div>
  );
}
